import React, { useCallback, useMemo, useState } from 'react';
import { Form, FormControl, FormGroup } from 'react-bootstrap';
import './index.scss';
import FeeMultiCurrencyInput from './FeeMultiCurrencyInput';
import DateTime from '../dateTime/DateTime';
import moment from 'moment';
import { ValidatorCustom } from '../validator';
import { CUSTOM_TYPE_INPUT, VALID_CASE } from '../../constants/commondata';
import FeeSingleCurrencyInput from './FeeSingleCurrencyInput';
import PropTypes from 'prop-types';
import CcCheckbox from '../ccCheckbox/CcCheckbox';
import InputGroupCustom from './InputGroupCustom';
import SelectMulti from '../SelectMulti';
import QuestionCircleTooltip from '../questionCircleTooltip/QuestionCircleTooltip';
import FormTitleCC from './component/FormTitleCC';
import { convertFieldByDistanceType } from './utils/formControlCustomFunc';
import { I18n } from 'react-redux-i18n';
import InputGroupSelectChild from './InputGroupSelectChild';

const FormControlCustom = ({
  idValidation,
  validatorCallback,
  valid,
  value,
  disabled,
  isSubmitted,
  handleChange,
  field = {},
  isHideTitle,
  currencies = [],
  currency = {},
  unitDistance,
  currencySymbol,
  isForcePercent,
  onChangeSelectAppend,
  valueSelectAppend,
  ...props
}) => {
  if (field.basedDistanceUnit) {
    field = convertFieldByDistanceType(field, unitDistance);
  }
  const [messageError, setMessageError] = useState('');
  const idValidationFinal = useMemo(() => {
    return idValidation || field?.key;
  }, []);

  const checkErrValidated = valid?.[idValidationFinal] === false && isSubmitted;
  const disabledFinal = useMemo(() => {
    return disabled || field?.disabled;
  }, [disabled]);

  const renderTitle = () => {
    return (
      <FormTitleCC
        title={field.title}
        toolTip={field.toolTip}
        isRequired={field?.validCases?.includes(VALID_CASE?.required)}
      />
    );
  };

  const renderFeeMultiCurrencyInput = () => (
    <FormGroup>
      {renderTitle()}
      <FeeMultiCurrencyInput
        currencies={currencies}
        isSubmitted={isSubmitted}
        validCases={field.validCases}
        name={field.key}
        validatorCallback={validatorCallback}
        handleChange={handleChange}
        feesByCurrencies={value || []}
      />
    </FormGroup>
  );

  const renderCCCheckBox = () => (
    <CcCheckbox
      text={field.title}
      checked={value}
      onChange={(e) => handleChange(e.target.checked)}
      // id={field.id || field.key}
      disabled={disabledFinal}
      // note={field.toolTip?.text}
      hasTooltip={field.toolTip?.text ? true : false}
      tooltipText={field.toolTip?.text}
    />
  );

  const renderSelectInput = () => (
    <select
      className="form-control form-custom"
      value={value}
      onChange={(e) => handleChange(e.target?.value)}
      disabled={disabledFinal}
      {...props}
    >
      {field?.option?.map((f) => (
        <option key={f.value} value={f.value}>
          {f.text || f.label}
        </option>
      ))}
    </select>
  );

  const renderFormControlInput = () => {
    let textGRPrependFinal = field.textGRPrepend;
    if(field.type === CUSTOM_TYPE_INPUT.FEE_SINGLE_CURRENCY) {
      textGRPrependFinal = field?.isForcePercent ? '%' : currencySymbol || currency?.symbol
    }
    return (
      <InputGroupCustom
        textGRAppend={field.textGRAppend}
        textGRPrepend={textGRPrependFinal}
        selectUnitAppend={field.selectUnitAppend}
      >
        <FormControl
          className="form-control form-custom"
          type={field.type === CUSTOM_TYPE_INPUT.FEE_SINGLE_CURRENCY ? 'number' : field.type}
          {...(field.as ? { as: field.as } : {})}
          value={value}
          onChange={(e) => handleChange(e.target?.value)}
          disabled={disabledFinal}
          placeholder={field.placeholder || ''}
        />
        {
          field?.selectUnitAppend?.key &&
          <InputGroupSelectChild
            value={valueSelectAppend}
            handleChange={onChangeSelectAppend}
            field={field.selectUnitAppend}
          />
        }
      </InputGroupCustom>
    )
  }

  const renderDateInput = () => {
    const handleDateChange = (e) => {
      handleChange(e?.format('YYYY-MM-DD') || null);
    };
    return (
      <DateTime
        value={moment(value)}
        timeFormat={false}
        onChange={handleDateChange}
        closeOnSelect={true}
        clearData
        inputProps={{
          readOnly: true,
          className: 'date-readonly form-control fromDateInvoice',
          placeholder: field.title,
          value: value ? moment(value)?.format('L') : '',
          disabled: disabledFinal,
        }}
      />
    );
  };

  const renderInput = () => {
    if (field.type === 'select') {
      if (field.isMultiSelect) {
        const normalizedValue = value?.map((val) => {
          const option = field.option.find((opt) => opt.value === val);
          return option
            ? { value: option.value, label: option.text || option.label }
            : { value: val, label: val };
        });
        return (
          <SelectMulti
            value={normalizedValue}
            error={checkErrValidated}
            className={checkErrValidated ? 'error' : ''}
            onChange={(selectedOptions) => {
              const normalizedOptions = selectedOptions.map((option) => {
                if (typeof option === 'object' && option !== null) {
                  return option.value;
                }
                return option;
              });
              handleChange(normalizedOptions);
            }}
            options={field.option.map((f) => ({
              value: f.value,
              label: f?.text || f?.label,
            }))}
            isMulti
            isDisabled={disabledFinal}
            placeholder={field.placeholder || I18n.t('cue.combobox_select')}
            {...props}
          />
        );
      }
      return renderSelectInput();
    }
    if (field.type === 'date') {
      return renderDateInput();
    }

    return renderFormControlInput();
  };

  if (field.type === CUSTOM_TYPE_INPUT.CC_CHECKBOX) {
    return renderCCCheckBox();
  }

  if (field.type === CUSTOM_TYPE_INPUT.FEE_MULTI_TYPE) {
    return renderFeeMultiCurrencyInput();
  }

  const handleValidatorCallback = useCallback((id, isValid, mess) => {
    if (validatorCallback) {
      validatorCallback(id, isValid, mess);
      setMessageError(mess);
    } else {
      setMessageError('');
    }
  }, []);

  return (
    <FormGroup
      className={`${checkErrValidated ? 'error' : ''} ${props?.classNameContainer} FormControlCustom`}
    >
      {renderTitle()}
      {
        field.text && (
          <Form.Text style={{marginBottom: '10px', marginTop: '0px', lineHeight: '1'}}>
            {field.text}
          </Form.Text>
        )
      }
      {renderInput()}
      {
        checkErrValidated && messageError && (
          <Form.Control.Feedback type="invalid">
            {messageError}
          </Form.Control.Feedback>
        )
      }
      <ValidatorCustom
        validatorCallback={handleValidatorCallback}
        id={idValidationFinal}
        validCases={field.validCases}
        value={value}
        isSubmitted={isSubmitted}
        isMultiSelect={field.isMultiSelect}
        rules={field.rules}
      />
      {/* {checkErrValidated && messageError && <div className="error-message">{messageError}</div>} */}
    </FormGroup>
  );
};

FormControlCustom.propTypes = {
  idValidation: PropTypes.string,
  validatorCallback: PropTypes.func,
  valid: PropTypes.object,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  handleChange: PropTypes.func,
  field: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.string,
    key: PropTypes.string,
    textNote: PropTypes.string,
    placeHolder: PropTypes.string,
    validCases: PropTypes.arrayOf(PropTypes.string),
    rules: PropTypes.object,
    option: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      text: PropTypes.string
    })),
    selectUnitAppend: PropTypes.shape({
      key: PropTypes.string,
      option: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      })),
    }),
    isMultiSelect: PropTypes.bool,
    textGRPrepend: PropTypes.string,
  }),
  isHideTitle: PropTypes.bool,
  currencies: PropTypes.array,
  currency: PropTypes.object,
  currencySymbol: PropTypes.string,
  selectUnitPrepend: PropTypes.object,
  textGRAppend: PropTypes.string,
  basedDistanceUnit: PropTypes.bool,
  toolTip: PropTypes.shape({
    text: PropTypes.string,
    type: PropTypes.string,
  }),

  // selectUnitAppend 
  valueSelectAppend: PropTypes.any,
  onChangeSelectAppend: PropTypes.func,
};

FormControlCustom.defaultProps = {
  disabled: false,
  isSubmitted: false,
  isHideTitle: false,
  currencies: [],
  currencySymbol: '$',
};

export default FormControlCustom;
